/**
 * quick-add-inline.js
 * Defines the <quick-add-inline> web component.
 *
 * Behavior:
 *  - Renders a circular "+" button overlaid on the card image (bottom-right).
 *  - Single-variant products: clicking "+" POSTs directly to /cart/add.js.
 *  - Multi-variant products WITH a "Size" option: clicking "+" reveals an inline
 *    slide-up panel showing Size pills. Clicking a pill adds the first available
 *    variant for that size. If the product also has other option dimensions
 *    (e.g. Color), a "More options" link falls back to the product page.
 *  - Multi-variant products WITHOUT a "Size" option (e.g. color-only): the "+"
 *    links directly to the PDP. Color swatches are already shown below the card
 *    image, so a color picker here would be redundant.
 *  - Customizable / personalized products (bypass tag): the "+" links to PDP.
 *  - Out-of-stock products: no "+" button is rendered (handled in Liquid).
 *
 * Cart integration:
 *  Mirrors product-form.js exactly — appends section IDs to FormData so Shopify
 *  returns rendered section HTML, then calls cart.renderContents(response) to
 *  open the cart drawer / cart-notification. Also fires PUB_SUB_EVENTS.cartUpdate
 *  for third-party subscribers.
 *
 * This file is completely additive — it does not modify any existing JS files.
 */

if (!customElements.get('quick-add-inline')) {
  customElements.define(
    'quick-add-inline',
    class QuickAddInline extends HTMLElement {
      constructor() {
        super();
        this.variantData = null;
        this.optionNames = [];
        this.isOpen = false;
        this.isLoading = false;
      }

      connectedCallback() {
        // Parse variant JSON embedded by the Liquid snippet
        const dataEl = this.querySelector('[data-quick-add-inline-variants]');
        if (dataEl) {
          try {
            const parsed = JSON.parse(dataEl.textContent);
            this.variantData = parsed.variants || parsed; // backwards-compat if old flat array
            this.optionNames = parsed.optionNames || [];
          } catch (e) {
            console.error('[QuickAddInline] Failed to parse variant data:', e);
          }
        }

        this.triggerBtn = this.querySelector('.qai-trigger');
        this.panel = this.querySelector('.qai-panel');
        this.pillsContainer = this.querySelector('.qai-pills');

        if (!this.triggerBtn) return;

        this.triggerBtn.addEventListener('click', this._onTriggerClick.bind(this));

        // Close panel when clicking outside this component
        document.addEventListener('click', this._onDocumentClick.bind(this));

        // Close on Escape
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.isOpen) this._closePanel();
        });
      }

      disconnectedCallback() {
        document.removeEventListener('click', this._onDocumentClick.bind(this));
      }

      _onDocumentClick(e) {
        if (this.isOpen && !this.contains(e.target)) {
          this._closePanel();
        }
      }

      _onTriggerClick(e) {
        e.preventDefault();
        e.stopPropagation();

        if (this.isLoading) return;

        // Bypass: customizable / personalized / no-size multi-variant — link to PDP
        if (this.triggerBtn.dataset.bypass === 'true') {
          const pdpUrl = this.triggerBtn.dataset.productUrl;
          if (pdpUrl) window.location.href = pdpUrl;
          return;
        }

        const variants = this.variantData;
        if (!variants || variants.length === 0) return;

        // Single variant: direct add (covers "Default Title", "One Size", etc.)
        if (variants.length === 1) {
          this._addToCart(variants[0].id);
          return;
        }

        // Multi-variant with Size: toggle the panel
        if (this.isOpen) {
          this._closePanel();
        } else {
          this._openPanel();
        }
      }

      _openPanel() {
        if (!this.panel) return;
        this._buildPills();
        this.isOpen = true;
        this.panel.classList.add('is-open');
        this.triggerBtn.classList.add('is-open');
        this.triggerBtn.setAttribute('aria-expanded', 'true');
      }

      _closePanel() {
        if (!this.panel) return;
        this.isOpen = false;
        this.panel.classList.remove('is-open');
        this.triggerBtn.classList.remove('is-open');
        this.triggerBtn.setAttribute('aria-expanded', 'false');
      }

      /**
       * Find the index (0-based) of an option by keyword (case-insensitive contains).
       * Returns -1 if not found.
       */
      _getOptionIndex(keyword) {
        return this.optionNames.findIndex(
          (name) => name.toLowerCase().includes(keyword.toLowerCase())
        );
      }

      /**
       * Build the panel contents.
       *
       * Products with Size only → single row of size pills (original behavior).
       * Products with Color only → single row of color pills; clicking adds directly.
       * Products with Color + Size → color row on top, size row below.
       *   Selecting a color re-renders the size row filtered to that color.
       */
      _buildPills() {
        if (!this.pillsContainer || !this.variantData) return;

        // Only rebuild if empty
        if (this.pillsContainer.children.length > 0) return;

        const variants = this.variantData;
        const sizeIdx = this._getOptionIndex('size');
        const colorIdx = this._getOptionIndex('color') >= 0
          ? this._getOptionIndex('color')
          : this._getOptionIndex('colour');

        const hasSizeOption = sizeIdx >= 0;
        const hasColorOption = colorIdx >= 0;

        if (hasColorOption) {
          // Build color row
          const colorKey = `option${colorIdx + 1}`;
          const seenColors = new Set();
          const uniqueColors = [];
          variants.forEach((v) => {
            const c = v[colorKey];
            if (c && !seenColors.has(c)) { seenColors.add(c); uniqueColors.push(c); }
          });

          const colorRow = document.createElement('div');
          colorRow.className = 'qai-row qai-row--color';

          // Default selected color: first color that has at least one available variant
          const defaultColor = uniqueColors.find((c) =>
            variants.some((v) => v[colorKey] === c && v.available)
          ) || uniqueColors[0];

          this._selectedColor = defaultColor;

          uniqueColors.forEach((color) => {
            // Use a <label> wrapper matching the PDP's .color-swatch markup so
            // the theme's CSS variables (size, border, radius) apply automatically.
            const pill = document.createElement('button');
            pill.type = 'button';
            // qai-pill--color for our selected/unavailable state logic;
            // color-swatch + overflow-hidden to inherit the theme's swatch styles.
            pill.className = 'qai-pill qai-pill--color color-swatch overflow-hidden';
            pill.setAttribute('aria-label', color);
            pill.dataset.color = color;

            // Exact same S3 URL pattern + onerror fallback as product-variant-options.liquid
            const colorSlug = color.toLowerCase().replace(/\//g, '-').replace(/\s+/g, '-');
            const swatchImg = document.createElement('img');
            swatchImg.src = `//snowdam.s3.us-east-2.amazonaws.com/swatch/${colorSlug}.png`;
            swatchImg.alt = '';
            // onerror: fall back to broken-image.jpg (same as PDP)
            swatchImg.onerror = function () {
              this.onerror = null;
              this.src = '//snowdam.s3.us-east-2.amazonaws.com/swatch/broken-image.jpg';
            };
            pill.appendChild(swatchImg);

            // Hidden text for screen readers
            const srText = document.createElement('span');
            srText.className = 'visually-hidden';
            srText.setAttribute('aria-hidden', 'true');
            srText.textContent = color;
            pill.appendChild(srText);

            const colorAvailable = variants.some((v) => v[colorKey] === color && v.available);
            if (!colorAvailable) {
              pill.classList.add('is-unavailable');
              pill.setAttribute('aria-disabled', 'true');
              pill.title = 'Sold out';
            }

            if (color === defaultColor) pill.classList.add('is-selected');

            pill.addEventListener('click', (e) => {
              e.stopPropagation();
              if (pill.classList.contains('is-unavailable')) return;

              // Update selected state
              colorRow.querySelectorAll('.qai-pill--color').forEach((p) => p.classList.remove('is-selected'));
              pill.classList.add('is-selected');
              this._selectedColor = color;

              // Update the card image to match the selected color variant,
              // so the card and the cart thumbnail show the same image.
              this._updateCardImage(variants, colorKey, color);

              if (hasSizeOption) {
                // Re-render size row for this color
                this._renderSizeRow(variants, sizeIdx, colorKey, color);
              } else {
                // Color-only: add the matching variant directly
                const match = variants.find((v) => v[colorKey] === color && v.available)
                  || variants.find((v) => v[colorKey] === color);
                if (match) {
                  this._addToCart(match.id);
                  this._closePanel();
                }
              }
            });

            colorRow.appendChild(pill);
          });

          this.pillsContainer.appendChild(colorRow);

          if (hasSizeOption) {
            // Size row placeholder — will be populated below
            const sizeRow = document.createElement('div');
            sizeRow.className = 'qai-row qai-row--size';
            this.pillsContainer.appendChild(sizeRow);
            this._renderSizeRow(variants, sizeIdx, colorKey, defaultColor);
          }
        } else if (hasSizeOption) {
          // Size-only: single row (original behavior)
          const sizeRow = document.createElement('div');
          sizeRow.className = 'qai-row qai-row--size';
          this.pillsContainer.appendChild(sizeRow);
          this._renderSizeRow(variants, sizeIdx, null, null);
        }
      }

      /**
       * Render (or re-render) the size pill row.
       * If colorKey + selectedColor are provided, filters variants to that color.
       */
      _renderSizeRow(variants, sizeIdx, colorKey, selectedColor) {
        const sizeRow = this.pillsContainer.querySelector('.qai-row--size');
        if (!sizeRow) return;
        sizeRow.innerHTML = '';

        const sizeKey = `option${sizeIdx + 1}`;
        const seenSizes = new Set();

        variants.forEach((variant) => {
          // Filter to selected color if applicable
          if (colorKey && selectedColor && variant[colorKey] !== selectedColor) return;

          const sizeVal = variant[sizeKey];
          if (!sizeVal || seenSizes.has(sizeVal)) return;
          seenSizes.add(sizeVal);

          // Best variant for this size (prefer available)
          const matchingVariant = variants.find(
            (v) => v[sizeKey] === sizeVal
              && (!colorKey || !selectedColor || v[colorKey] === selectedColor)
              && v.available
          ) || variants.find(
            (v) => v[sizeKey] === sizeVal
              && (!colorKey || !selectedColor || v[colorKey] === selectedColor)
          );

          const pill = document.createElement('button');
          pill.type = 'button';
          pill.className = 'qai-pill';
          pill.textContent = sizeVal;
          pill.dataset.variantId = matchingVariant ? matchingVariant.id : '';

          if (!matchingVariant || !matchingVariant.available) {
            pill.classList.add('is-unavailable');
            pill.setAttribute('aria-disabled', 'true');
            pill.title = 'Sold out';
          }

          pill.addEventListener('click', (e) => {
            e.stopPropagation();
            if (pill.classList.contains('is-unavailable')) return;
            const variantId = parseInt(pill.dataset.variantId, 10);
            if (variantId) {
              // Sync card image to the variant being added so the card and
              // cart thumbnail always show the same image (fixes first-item mismatch).
              if (matchingVariant && matchingVariant.featured_image_url) {
                const card = this.closest('.cg-card');
                const cardImg = card && card.querySelector('[data-card-image]');
                if (cardImg) {
                  cardImg.srcset = '';
                  cardImg.src = matchingVariant.featured_image_url;
                }
              }
              this._addToCart(variantId);
              this._closePanel();
            }
          });

          sizeRow.appendChild(pill);
        });
      }

      _addToCart(variantId) {
        if (this.isLoading) return;
        this.isLoading = true;
        this._setLoading(true);

        // Prefer cart-drawer (polished slide-out UI) over cart-notification.
        // When quick_view_style == 'inline', cart-drawer is always rendered in the
        // DOM (see layout/theme.liquid + sections/header.liquid), so this will
        // reliably find it even on stores whose cart_type is "notification".
        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', 1);

        // Append section IDs so Shopify returns rendered section HTML in the
        // response — required for cart.renderContents() to work.
        if (cart && typeof cart.getSectionsToRender === 'function') {
          formData.append(
            'sections',
            cart.getSectionsToRender().map((s) => s.id).join(',')
          );
          formData.append('sections_url', window.location.pathname);
          if (typeof cart.setActiveElement === 'function') {
            cart.setActiveElement(document.activeElement);
          }
        }

        // Use fetchConfig if available (defined in global.js), otherwise build manually
        let config;
        if (typeof fetchConfig === 'function') {
          config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
        } else {
          config = {
            method: 'POST',
            headers: {
              Accept: 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest',
            },
          };
        }
        config.body = formData;

        const cartAddUrl =
          (window.routes && window.routes.cart_add_url) ||
          (window.Shopify && window.Shopify.routes && window.Shopify.routes.root + 'cart/add.js') ||
          '/cart/add.js';

        fetch(cartAddUrl, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              // Error returned by Shopify (e.g. sold out, limit reached)
              if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'quick-add-inline',
                  productVariantId: variantId,
                  errors: response.errors || response.description,
                  message: response.message,
                });
              }
              return;
            }

            // Fire the same pub-sub event that product-form.js fires
            if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'quick-add-inline',
                productVariantId: variantId,
                cartData: response,
              });
            }

        // Open / refresh the cart UI.
        // Scroll position is preserved by cart-drawer.js open()/close() which
        // saves window.scrollY into body.style.top before applying overflow-hidden.
        if (cart) {
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          if (typeof cart.renderContents === 'function') {
            cart.renderContents(response);
          }
        }
          })
          .catch((error) => {
            console.error('[QuickAddInline] Add to cart error:', error);
          })
          .finally(() => {
            this.isLoading = false;
            this._setLoading(false);
          });
      }

      /**
       * Update the card's hero image to match the selected color variant.
       * This ensures the card image and the cart thumbnail show the same product.
       *
       * Looks for [data-card-image] in the closest .cg-card ancestor.
       * Only swaps if the variant has a featured_image_url (set in Liquid).
       */
      _updateCardImage(variants, colorKey, selectedColor) {
        const card = this.closest('.cg-card');
        if (!card) return;
        const cardImg = card.querySelector('[data-card-image]');
        if (!cardImg) return;

        // Find the first variant for this color that has an image
        const match = variants.find(
          (v) => v[colorKey] === selectedColor && v.featured_image_url
        ) || variants.find(
          (v) => v[colorKey] === selectedColor
        );

        if (!match || !match.featured_image_url) return;

        // Swap src — clear srcset so the browser uses the new src immediately
        cardImg.srcset = '';
        cardImg.src = match.featured_image_url;
        cardImg.alt = selectedColor;
      }

      _setLoading(loading) {
        if (!this.triggerBtn) return;
        const spinner = this.triggerBtn.querySelector('.qai-spinner');
        const icon = this.triggerBtn.querySelector('.qai-icon');

        if (loading) {
          this.triggerBtn.setAttribute('aria-disabled', 'true');
          if (spinner) spinner.classList.remove('hidden');
          if (icon) icon.classList.add('hidden');
        } else {
          this.triggerBtn.removeAttribute('aria-disabled');
          if (spinner) spinner.classList.add('hidden');
          if (icon) icon.classList.remove('hidden');
        }
      }
    }
  );
}
